import type { Permission } from '@ea/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { api, ApiError } from './api';

export interface Me {
  id: string;
  email: string;
  fullName: string;
  title: string | null;
  roles: { key: string; label: string }[];
  permissions: Permission[];
  scope: 'none' | 'assigned' | 'department' | 'organization' | 'finance' | 'representative';
  departmentIds: string[];
  reportIds: string[];
  /** Present while "View as" is active: the real signed-in person. */
  viewer?: { id: string; fullName: string; asRole?: boolean; canAct?: boolean };
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  can: (p: Permission) => boolean;
  hasRole: (r: string) => boolean;
  refresh: () => Promise<unknown>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(() => {
    const onUnauthorized = () => qc.setQueryData(['me'], null);
    window.addEventListener('ea:unauthorized', onUnauthorized);
    return () => window.removeEventListener('ea:unauthorized', onUnauthorized);
  }, [qc]);

  const me = q.data ?? null;
  const value: AuthState = {
    me,
    loading: q.isLoading,
    can: (p) => !!me?.permissions.includes(p),
    hasRole: (r) => !!me?.roles.some((x) => x.key === r),
    refresh: () => q.refetch(),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

/**
 * The company owner (Aman): system administrator + executive oversight. His approval at any approval stage is final
 * ("Approve (final)"); at or above the owner threshold it needs his authenticator code. Jason is not the owner.
 */
export function useIsOwner() {
  const { hasRole } = useAuth();
  return hasRole('system_administrator') && hasRole('executive_oversight');
}

/** The landing dashboard for the signed-in employee. */
export function homePath(me: Me | null): string {
  if (!me) return '/login';
  const p = me.permissions;
  if (me.roles?.some((r) => r.key === 'address_representative')) return '/rep';
  // Client-portal users; client case manager (Arju) and banker-call specialist (Paul) start on their service queues.
  // Only a pure client account opens the portal; staff given the client role by mistake keep their staff home.
  if (me.roles?.length && me.roles.every((r) => r.key === 'client')) return '/portal';
  if (me.roles?.some((r) => r.key === 'client_case_manager')) return '/services';
  if (me.roles?.some((r) => r.key === 'banker_call_specialist')) return '/services/banker';
  // The administrator holds every permission; start on the company-wide overview.
  if (me.roles?.some((r) => r.key === 'system_administrator')) return '/executive';
  if (p.includes('dashboard.operations')) return '/workspace';
  if (p.includes('dashboard.shipping_management') && !p.includes('dashboard.supervisor')) return '/management';
  if (p.includes('dashboard.supervisor') && !p.includes('dashboard.executive')) return '/supervisor';
  if (p.includes('dashboard.executive')) return '/executive';
  if (p.includes('finance.read') && p.includes('dashboard.finance')) return '/finance';
  if (p.includes('dashboard.finance')) return '/payment-processing';
  if (p.includes('admin.users')) return '/admin/users';
  return '/notifications';
}
